import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "./cn";

type Tone = "default" | "elevated" | "glow";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: Tone;
  /** Render with the conic-gradient luminous border. */
  glow?: boolean;
  as?: "div" | "section" | "article" | "aside";
}

const TONE: Record<Tone, string> = {
  default: "glass",
  elevated: "glass-strong",
  glow: "glass-strong glass-glow",
};

/** Glass panel with three elevation tiers + optional gradient border halo. */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { tone = "default", glow, as = "section", className, children, ...rest },
  ref,
) {
  const Tag = as as "section";
  const cls = glow ? TONE.glow : TONE[tone];
  return (
    <Tag ref={ref} className={cn(cls, className)} {...rest}>
      {children}
    </Tag>
  );
});

interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
}

export function CardHeader({
  title,
  subtitle,
  icon,
  actions,
  className,
  children,
  ...rest
}: CardHeaderProps) {
  return (
    <header
      className={cn(
        "flex items-center justify-between gap-3 border-b border-line-soft/60 px-4 py-3 sm:px-5",
        className,
      )}
      {...rest}
    >
      <div className="flex min-w-0 items-center gap-3">
        {icon ? (
          <span
            className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-[linear-gradient(135deg,hsl(var(--accent)/0.18),hsl(var(--accent-2)/0.12))] text-accent-300 ring-1 ring-[hsl(var(--accent)/0.35)]"
            aria-hidden
          >
            {icon}
          </span>
        ) : null}
        <div className="min-w-0">
          {title ? (
            <div className="truncate text-sm font-semibold text-fg-strong">{title}</div>
          ) : null}
          {subtitle ? (
            <div className="truncate text-xs text-fg-subtle">{subtitle}</div>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      {children}
    </header>
  );
}

export function CardBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-4 sm:p-5", className)} {...rest} />;
}
